import { createClient } from "@supabase/supabase-js";
import { StorageApiError } from "@supabase/storage-js";
import { ImpersonationService } from "./impersonationService";
import { createErrorFromSupabaseError, retryWrapper } from "./retryWrapper";
import {
    CreatorPromptField,
    PatientRecord,
    ChatCompletions,
    Transcript,
    TranscriptSection,
    TranscriptSectionStatus,
    AccountIntegration,
    DocumentType,
    PatientRecordFetchErrors,
    DocumentStatus,
    ExamplePrompt,
    CreatorPromptFieldCategory,
    CustomFieldMeta,
    AppVersion,
    Account,
    Communications,
    PartialDocumentType,
    Address,
    AccountMetaData,
    VisitPage,
    DataMapping,
    Contact,
    Patient,
    DocumentAsset,
    ChatCompletionAsset,
    VisitHeader,
    DoctorDocumentTypeContext,
    PatientRecordArchivedBy,
    DocumentExport,
    DeletePatientRecordOutcome,
    DeletePatientRecordsResult,
} from "../utils/types";
import isUUID from "validator/lib/isUUID";
import isDate from "validator/lib/isDate";
import { getStoredSelectedClinicAccountId } from "@common/lib/selectedClinicAccountStorage";
import { invalidateDocumentCache, transcribeAudio } from "./api";
import { VITE_PUBLIC_SUPABASE_URL, VITE_PUBLIC_SUPABASE_ANON_KEY } from "@common/utils/constants";
import { v4 as uuidv4 } from "uuid";
import * as Sentry from "@sentry/react";
import { Database } from "../utils/database.types";
import { filterMonitoringDocumentTypeByFlag } from "../utils/monitoring";
import { pathJoin } from "../utils/helpers";
import { classifyNetworkError } from "./sentryUtils";

const supabaseUrl = VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = VITE_PUBLIC_SUPABASE_ANON_KEY;

/**
 * Supabase response types -----------------------------
 */
export type SupabaseResponse<T> = { data: T; error: null } | { data: null; error: Error };

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey);

/**
 * Checks if a document type is an INSIGHTS document
 * @param documentType - The document type to check (can be partial with just custom_document_type)
 * @returns True if the document is an INSIGHTS document
 */
export function isInsightsDocument(documentType: { custom_document_type?: string | null } | null | undefined): boolean {
    return documentType?.custom_document_type === "INSIGHTS";
}

/**
 * Filters out INSIGHTS document types from an array
 * Used for post-fetch filtering when querying nested relations through document_sharing,
 * since PostgREST's .or() method doesn't support filtering on nested relation columns.
 * @param documents - Array of document types to filter
 * @returns Filtered array without INSIGHTS documents
 */
function filterOutInsightsDocuments(documents: DocumentType[]): DocumentType[] {
    return documents.filter((doc) => !isInsightsDocument(doc));
}

// APP VERSIONS
export const getAppVersion = async (signal: AbortSignal): Promise<AppVersion | null> => {
    // Do NOT use retryWrapper here. This call uses an AbortSignal that fires intentionally on
    // every route change (CacheBuster cleanup). retryWrapper misidentifies the resulting
    // AuthRetryableFetchError as a network error, logs it to Sentry, and retries 3 times
    // against an already-aborted signal — generating noise for expected cancellations.
    try {
        const { data, error } = await supabase
            .from("app_versions")
            .select("id, version")
            .abortSignal(signal)
            .eq("id", 1)
            .single();
        if (error) return null;
        return (data as unknown as AppVersion) || null;
    } catch {
        // Swallow thrown errors (AbortError / AuthRetryableFetchError when signal fires)
        return null;
    }
};

// DOCTORS
// Note: Doctor CRUD operations have been migrated to API Server and React Query
// See src/state/hooks/useDoctors.ts and src/common/lib/api.ts

// SELECTED DOCUMENTS

export const addSelectedDocumentForDoctor = async (doctorId: string, documentTypeId: string): Promise<any | null> => {
    // Get effective session for impersonation
    const { ImpersonationService } = await import("./impersonationService");
    const effectiveSession = await ImpersonationService.getEffectiveSession();

    if (!effectiveSession) {
        console.error("No effective session found for adding selected document for doctor");
        return null;
    }

    const { error } = await supabase
        .from("doctor_document_types")
        .insert({ doctor_id: doctorId, document_type_id: documentTypeId, context: DoctorDocumentTypeContext.Default })
        .select();

    if (error) {
        if (error?.message?.includes("duplicate key value violates unique constraint")) {
            return null;
        }
        return error;
    }

    return null;
};

export const addSelectedHistoryDocumentForDoctor = async (
    doctorId: string,
    documentTypeId: string,
): Promise<any | null> => {
    // Get effective session for impersonation
    const { ImpersonationService } = await import("./impersonationService");
    const effectiveSession = await ImpersonationService.getEffectiveSession();

    if (!effectiveSession) {
        console.error("No effective session found for adding selected history document for doctor");
        return null;
    }

    const { error } = await supabase
        .from("doctor_document_types")
        .insert({ doctor_id: doctorId, document_type_id: documentTypeId, context: DoctorDocumentTypeContext.History })
        .select();

    if (error) {
        if (error?.message?.includes("duplicate key value violates unique constraint")) {
            return null;
        }
        return error;
    }

    return null;
};

export const removeSelectedDocumentForDoctor = async (
    doctorId: string,
    documentTypeId: string,
): Promise<any | null> => {
    // Get effective session for impersonation
    const { ImpersonationService } = await import("./impersonationService");
    const effectiveSession = await ImpersonationService.getEffectiveSession();

    if (!effectiveSession) {
        console.error("No effective session found for removing selected document for doctor");
        return null;
    }

    const { error } = await supabase
        .from("doctor_document_types")
        .delete()
        .eq("doctor_id", doctorId)
        .eq("document_type_id", documentTypeId)
        .eq("context", DoctorDocumentTypeContext.Default);

    if (error) {
        return error;
    }

    return null;
};

export const removeSelectedHistoryDocumentForDoctor = async (
    doctorId: string,
    documentTypeId: string,
): Promise<any | null> => {
    // Get effective session for impersonation
    const { ImpersonationService } = await import("./impersonationService");
    const effectiveSession = await ImpersonationService.getEffectiveSession();

    if (!effectiveSession) {
        console.error("No effective session found for removing selected history document for doctor");
        return null;
    }

    const { error } = await supabase
        .from("doctor_document_types")
        .delete()
        .eq("doctor_id", doctorId)
        .eq("document_type_id", documentTypeId)
        .eq("context", DoctorDocumentTypeContext.History);

    if (error) {
        return error;
    }

    return null;
};

// Helper function to get effective session for impersonation
const getEffectiveSession = async () => {
    try {
        return await ImpersonationService.getEffectiveSession();
    } catch (error) {
        console.error("Error getting effective session:", error);
        return null;
    }
};

/**
 * Resolves which id to use for document/account-scoped Supabase queries.
 * - Normal session: prefer explicit `accountID` from AccountProvider (multi-clinic; team member auth id ≠ clinic account id).
 * - Impersonation: prefer `effectiveSession.userId` (target user) so a stale caller `accountID` from the admin context is not used.
 */
function resolveDocumentScopeAccountId(
    accountID: string,
    effectiveSession: { userId: string; isImpersonating: boolean },
): string {
    if (effectiveSession.isImpersonating) {
        return effectiveSession.userId || accountID;
    }
    return accountID || effectiveSession.userId;
}

/**
 * Same clinic `account.id` resolution as {@link getSupabaseDocumentTypes} (reads).
 * Used for document_types inserts so team members write `account_id` as the clinic row, not auth user id.
 */
async function resolveDocumentAccountIdForWrite(abortSignal: AbortSignal): Promise<string | null> {
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) return null;
    const selectedClinicId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(selectedClinicId ?? "", effectiveSession);
    if (!effectiveAccountID) return null;
    try {
        const accountData = await fetchAccountData(abortSignal, effectiveAccountID);
        return accountData?.id ?? effectiveAccountID;
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return null;
        }
        throw error;
    }
}

// DOCUMENT TYPES
export const getSupabaseDocumentTypes = async (
    abortSignal: AbortSignal,
    hiddenDocIDs: string[],
    accountID: string,
): Promise<DocumentType[]> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for getSupabaseDocumentTypes");
        return [];
    }

    const effectiveAccountID = resolveDocumentScopeAccountId(accountID, effectiveSession);

    if (!effectiveAccountID) {
        return [];
    }

    // Fetch account data to resolve clinic account id (team members: auth user id ≠ document account_id).
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(abortSignal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return [];
        }
        throw error;
    }
    const documentAccountId = accountData?.id ?? effectiveAccountID;

    const { data, error } = await retryWrapper(async () => {
        let query = supabase
            .from("document_types")
            .select(
                `id,
			 document_type_name,
			 account_id,
			 auto_generate,
			 is_default,
			 description,
			 last_updated_at,
			 is_archived,
			 prompt_components,
			 training_data,
			 is_active,
			 custom_document_type,
			 creator_prompt_components,
			 is_json,
			 document_foundation_id,
			 output_language
			`,
            )
            .abortSignal(abortSignal)
            .eq("account_id", documentAccountId)
            .filter("is_archived", "eq", false);

        // Always exclude INSIGHTS document types (never user-facing).
        // Use .or() to include NULL values (since NULL <> 'INSIGHTS' evaluates to NULL in SQL)
        query = query.or("custom_document_type.is.null,custom_document_type.neq.INSIGHTS");

        return await query;
    });

    if (error || !data) {
        return [];
    }
    const sharedDocuments = await getSharedDocuments(abortSignal, documentAccountId);
    // if it is hidden, set is_active to false
    sharedDocuments.forEach((doc: any) => {
        if (hiddenDocIDs.includes(doc.id)) {
            doc.is_active = false;
        }
    });

    // create a Map to deduplicate documents by ID
    const documentMap = new Map();

    // first add account's own documents
    (data as DocumentType[]).forEach((doc: DocumentType) => {
        documentMap.set(doc.id, doc);
    });

    // then add shared documents, only if they don't already exist
    sharedDocuments.forEach((doc: DocumentType) => {
        if (!documentMap.has(doc.id)) {
            documentMap.set(doc.id, doc);
        }
    });

    // convert Map values back to array
    const allDocs = Array.from(documentMap.values()) as DocumentType[];

    return filterMonitoringDocumentTypeByFlag(allDocs, accountData?.monitoring_enabled);
};

export const getSharedDocuments = async (signal: AbortSignal, accountID: string): Promise<DocumentType[]> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for getSharedDocuments");
        return [];
    }

    // Use accountID directly - caller has already resolved it to the clinic account ID
    const effectiveAccountID = accountID;

    const { data, error } = await retryWrapper(async () => {
        const query = supabase
            .from("document_sharing")
            .select(
                `
		document_types (
			id,
			account_id,
			document_type_name,
			auto_generate,
			is_default,
			description,
			last_updated_at,
			is_archived,
			prompt_components,
			training_data,
			is_active,
			custom_document_type,
			creator_prompt_components,
			is_json,
			document_foundation_id,
			is_archived,
			output_language
		)
		`,
            )
            .abortSignal(signal)
            // filter out documents owned by the current account since we already have those
            .not("owner_account_id", "eq", effectiveAccountID)
            // include documents that are either:
            // 1. Publicly shared aka default documents (target_account_id is null)
            // 2. Specifically shared with this account (target_account_id is the current account)
            .or(`target_account_id.is.null,target_account_id.eq.${effectiveAccountID}`)
            // only include documents that can be generated
            .filter("can_generate", "eq", true)
            // exclude archived documents
            .filter("document_types.is_archived", "eq", false);

        // Note: We cannot filter on nested relation columns (document_types.custom_document_type)
        // using PostgREST's .or() method when querying from document_sharing table.
        // Instead, we rely on post-fetch filtering below.

        return await query;
    });

    if (error || !data) {
        return [];
    }

    const sharedDocs = data.map((doc) => doc.document_types).filter((doc) => doc) as DocumentType[];

    // Always exclude INSIGHTS document types — never user-facing (post-fetch filter for nested relations)
    const filteredSharedDocs = filterOutInsightsDocuments(sharedDocs);

    return filteredSharedDocs || [];
};

export const getDocumentTemplates = async (signal: AbortSignal, accountID: string): Promise<DocumentType[]> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for getDocumentTemplates");
        return [];
    }

    // Use accountID directly - caller has already resolved it to the clinic account ID
    const effectiveAccountID = accountID;

    // Verify account access (resolve/authorize the clinic account id).
    try {
        await fetchAccountData(signal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return [];
        }
        throw error;
    }

    const { data, error } = await retryWrapper(async () => {
        const query = supabase
            .from("document_sharing")
            .select(
                `
			document_types (
				id,
				account_id,
				document_type_name,
				auto_generate,
				is_default,
				description,
				last_updated_at,
				is_archived,
				prompt_components,
				training_data,
				is_active,
				custom_document_type,
				creator_prompt_components,
				is_json,
				document_foundation_id,
				output_language
			)
			`,
            )
            .abortSignal(signal)
            .filter("can_clone", "eq", true);

        // Note: We cannot filter on nested relation columns (document_types.custom_document_type)
        // using PostgREST's .or() method when querying from document_sharing table.
        // Instead, we rely on post-fetch filtering below.

        return await query;
    });

    if (error || !data) {
        return [];
    }

    const templates = data.map((doc: any) => doc.document_types).filter((doc) => doc) as DocumentType[];

    // Always exclude INSIGHTS document types — never user-facing (post-fetch filter for nested relations)
    const filteredTemplates = filterOutInsightsDocuments(templates);

    return filteredTemplates || [];
};

// retrieves the ids of all shared documents that the user owns
export const getSharedDocIDs = async (signal: AbortSignal, _ownerAccountID: string): Promise<string[]> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("document_sharing")
            .select(
                `
			document_types (
				id
			)
			`,
            )
            .abortSignal(signal);
    });

    if (error) {
        return [];
    }

    const ownedSharedDocIds = data?.map((doc: any) => doc.document_types?.id) as string[] as string[];

    return ownedSharedDocIds || [];
};

export const getDocumentTypesByIdsList = async (ids: string[]): Promise<PartialDocumentType[]> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("document_types")
            .select(
                `id,
				is_archived,
				document_type_name,
			 	is_json`,
            )
            .in("id", ids);
    });

    if (error) {
        return [];
    }

    return data || [];
};

// Fetching a single document type
export const getSingleSupabaseDocumentType = async (
    id: string,
    abortSignal: AbortSignal,
    accountID?: string,
): Promise<DocumentType | null> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession || !effectiveSession.userId) {
        console.error("No effective account ID found for getSingleSupabaseDocumentType");
        return null;
    }

    const storedAccountId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(
        accountID || storedAccountId || effectiveSession.userId,
        effectiveSession,
    );

    // Fetch account data to resolve clinic account id (team members: auth user id ≠ document account_id).
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(abortSignal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return null;
        }
        throw error;
    }
    const documentAccountId = accountData?.id ?? effectiveAccountID;

    const { data, error } = await retryWrapper<DocumentType>(async () => {
        return await supabase
            .from("document_types")
            .select(
                `
		  id, 
		  account_id,
		  document_type_name, 
		  prompt, 
		  prompt_components, 
		  output_parser, 
		  prompt_override, 
		  training_data, 
		  creator_prompt_components, 
		  custom_document_type, 
		  description, 
		  is_archived,
		  is_json, 
		  is_default,
		  document_foundation_id,
		  output_language,
		  document_exports (
			id,
			is_active,
			is_custom,
			font_family,
			template_key,
			note_type,
			cornerstone_alert_color,
			cornerstone_alert_disabled,
			shepherd_template_id,
			shepherd_template_name,
			shepherd_template_field_map,
			ezyvet_section_map
		  )
		`,
            )
            .abortSignal(abortSignal)
            .eq("id", id)
            .eq("document_exports.account_id", documentAccountId)
            .maybeSingle();
    });

    if (data && data.document_exports && Array.isArray(data.document_exports)) {
        // Force one-to-one relationship
        (data as any).document_exports = data.document_exports[0] || null;
    }

    if (error) {
        console.error("Error getting single document type:", error);
        // Error is already logged in the wrapper
        return null;
    }

    // INSIGHTS document types are never user-facing.
    if (data && data.custom_document_type === "INSIGHTS") {
        return null;
    }

    return data || null;
};

export const toggleDocumentSupabase = async (id: string, isActive: boolean) => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("document_types").update({ is_active: isActive }).eq("id", id);
    });

    return error;
};

// DEPRECATED - replaced by api/documentTypes.ts (EN-548). Kept for rollback; remove in follow-up.
export const saveNewDocumentType = async (
    documentType: Partial<DocumentType>,
    abortSignal?: AbortSignal,
): Promise<DocumentType | null> => {
    const documentAccountId = await resolveDocumentAccountIdForWrite(abortSignal ?? new AbortController().signal);
    if (!documentAccountId) {
        Sentry.captureException(new Error("Could not resolve clinic account id for saving document type"), {
            tags: { source: "saveNewDocumentType", reason: "no_account_id" },
        });
        return null;
    }

    const documentTypeWithAccountId = {
        ...documentType,
        account_id: documentAccountId,
    };

    const { error } = await retryWrapper(async () => {
        return await supabase.from("document_types").insert(documentTypeWithAccountId as any);
    });

    if (error) {
        Sentry.captureException(error, { tags: { source: "saveNewDocumentType" } });
        return null;
    }

    return (documentTypeWithAccountId as DocumentType) || null;
};

// DEPRECATED - replaced by api/documentTypes.ts (EN-548). Kept for rollback; remove in follow-up.
export const updateDocumentType = async (
    documentType: Partial<DocumentType>,
    isJson = true,
    updateDependencies: boolean,
): Promise<boolean> => {
    if (!documentType.id) {
        Sentry.captureException(new Error("updateDocumentType called without document id"), {
            tags: { source: "updateDocumentType", reason: "no_id" },
        });
        return false;
    }

    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        Sentry.captureException(new Error("No effective session found for updating document type"), {
            tags: { source: "updateDocumentType", reason: "no_session" },
        });
        return false;
    }

    let docUpdt = documentType;
    if (updateDependencies) {
        docUpdt = {
            ...documentType,
            dependency_data: {
                needsUpdate: true,
                dependencies: {},
            },
        };
    }

    // Never send account_id on update: it would overwrite with the wrong id for team members (auth user
    // vs clinic account) and could corrupt shared document types. Row account_id stays as stored.
    const { account_id: _accountIdOmitted, ...docUpdtForUpdate } = docUpdt as Partial<DocumentType> & {
        account_id?: string;
    };

    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("document_types")
            .update(docUpdtForUpdate as any)
            .eq("id", documentType.id as string);
    });

    if (error) {
        Sentry.captureException(error, { tags: { source: "updateDocumentType" } });
        return false;
    }

    if (!isJson) {
        await invalidateDocumentCache(documentType.id || "");
        return true;
    }

    return true;
};

export const updateDocumentTypeInclusion = async (docTypeId: string, isActive: boolean): Promise<boolean> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("document_types").update({ is_active: isActive }).eq("id", docTypeId);
    });

    if (error) {
        return false;
    }

    return true;
};

export const deleteDocumentFromSupabase = async (id: any): Promise<boolean> => {
    // set is_archived to true instead of deleting
    const { error } = await retryWrapper(async () => {
        return await supabase.from("document_types").update({ is_archived: true }).eq("id", id);
    });

    if (error) {
        return false;
    }

    return true;
};

// CONTACTS
export const updateContact = async (
    contact: Contact,
    id: string,
): Promise<{ data: Contact | null; error: any | null }> => {
    const { error } = await supabase
        .from("contacts")
        .update({
            ...contact,
        })
        .eq("id", id);

    if (error) {
        return { data: null, error: error };
    }
    return { data: contact || null, error: null };
};

// PATIENTS
export const updatePatient = async (
    patient: Patient,
    id: string,
): Promise<{ data: Patient | null; error: any | null }> => {
    const { error } = await supabase
        .from("patients")
        .update({
            ...patient,
        })
        .eq("id", id);

    if (error) {
        return { data: null, error: error };
    }
    return { data: patient, error: null };
};

//PATIENT RECORDS
export const updatePatientRecord = async (
    patientRecord: PatientRecord,
    transcriptionId: string | null = null,
): Promise<PatientRecord | null> => {
    // EN-1494 review: scoped to the resolved clinic like delete and stage; admins bypass RLS, so this
    // filter is the only gate keeping an inline edit, header edit or calendar drag inside the clinic.
    const clinic = await resolveClinicForPatientRecordWrite();
    if (!clinic) {
        throw new Error(`No clinic to scope the update of patient record ${patientRecord.id} to`);
    }

    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("patient_record")
            .update({
                ...(patientRecord as any),
            })
            .eq("id", patientRecord.id)
            .eq("account_id", clinic.accountId)
            .select();
    });

    // A genuine Supabase/PostgREST error is a real failure — throw so the sole caller
    // (patientTableRecordsSlice.ts's updatePatientRecord thunk) can distinguish it from
    // the ambiguous case below.
    if (error) {
        throw new Error(error.message ?? "Failed to update patient record");
    }

    if (transcriptionId) {
        await upsertTranscription({
            id: transcriptionId,
            patient_record_id: patientRecord?.id,
            doctor_id: patientRecord?.doctor_id,
        });
    }

    // retryWrapper deliberately swallows an aborted/cancelled request into a literal
    // { data: null, error: null } (see retryWrapper.ts) — Supabase/PostgREST reported no
    // error, so this is not a genuine failure, just an indeterminate result (the request
    // never completed because the caller navigated away or the component unmounted). Echo
    // the input back rather than null so the caller doesn't treat this the same as a real
    // error.
    if (data === null) {
        return patientRecord;
    }

    // A real, completed query that matches zero rows returns an empty ARRAY with no
    // error — a genuinely different shape from the abort sentinel above, and a genuine
    // failure to persist (RLS silently filtering the row, a stale account_id after a
    // clinic switch, or the record having been deleted/archived concurrently). Collapsing
    // this into the same "echo the input" path as the abort case (data?.[0] || patientRecord
    // did exactly that) would let the caller's optimistic UI keep showing a value that was
    // never actually saved, with no toast, no revert, and no way to notice until reload.
    if (Array.isArray(data) && data.length === 0) {
        if (!clinic.verified) reportUnverifiedClinicWrite("update", patientRecord.id, clinic.accountId);
        throw new Error(`Update matched zero rows for patient record ${patientRecord.id}`);
    }

    return data[0] as PatientRecord;
};

export const PATIENT_RECORD_SELECT = `
            id,
            is_history,
            patient_name,
            patient_id,
			contact_id,
            created_at,
			updated_at,
            doctors (
                id,
                doctor_name,
				pms_employee_id,
				pms_export_id
            ),
            resource_id,
            excluded_resource_id,
            status,
			linked_by_integration,
			scheduled_at,
			stage,
			pims_type_color,
			species,
			gender,
			chat_completions (
				id,
				created_at,
				role,
				type,
				chat_json,
				document_snapshot,
				transcription_id,
				document_type_id,
				patient_record_id,
				feedback,
				exported_at,
				exported_to,
				status,
				header,
				is_edited,
				sent_at,
				monitoring_date_key,
				document_types (
					id,
					is_json,
					creator_prompt_components,
					document_type_name,
					custom_document_type,
					document_exports (
						id,
						is_active
					)
				)
			),
			transcriptions (
                id,
				transcript_sections (
					id,
					unprocessed_filename,
					processed_filename,
					data,
					created_at,
					audio_duration,
					status,
					display_name,
					is_archived,
					origin,
					account_id
				)
          	),
			patients (
				id,
				name,
				sex,
				breed,
				dob,
				species,
				secondary_id,
				account_integrations_id
			),
			account_integrations (
				id,
				connected_third_party,
				is_active,
				integrations (
					integration_name
				)
			),
			contacts (
				id,
				first_name,
				last_name,
				primary_phone,
				primary_email,
				phones
			),
			document_assets (
				id,
				created_at,
				file_name,
				file_size,
				file_type,
				storage_path
			)
            `;

export const fetchPatientRecord = async (
    patient_record_id: string,
    accountID?: string,
    abortSignal?: AbortSignal,
): Promise<{ data: PatientRecord | null; error: any | null }> => {
    // check that patient_record_id is actually a uuid and not null
    if (!patient_record_id || !isUUID(patient_record_id)) {
        return { data: null, error: PatientRecordFetchErrors.NO_PATIENT_RECORD };
    }

    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession || !effectiveSession.userId) {
        console.error("No effective account ID found for fetchPatientRecord");
        return { data: null, error: new Error("No effective session found") };
    }

    const storedAccountId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(
        accountID || storedAccountId || effectiveSession.userId,
        effectiveSession,
    );

    // Resolve clinic account id for multi-clinic team members (auth user id ≠ clinic account id)
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(abortSignal ?? new AbortController().signal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return { data: null, error: new Error("Account access denied") };
        }
        throw error;
    }
    const queryAccountId = accountData?.id ?? effectiveAccountID;

    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("patient_record")
            .select(PATIENT_RECORD_SELECT)
            .eq("id", patient_record_id)
            .eq("account_id", queryAccountId)
            .eq("is_archived", false)
            .eq("chat_completions.is_archived", false)
            .eq("chat_completions.document_types.document_exports.account_id", queryAccountId)
            .eq("transcriptions.transcript_sections.is_archived", false)
            .eq("document_assets.is_archived", false);
    });

    if (data?.length === 0) {
        // patient record not found
        // this happens if a user deletes a patient record then hits the back button
        return { data: null, error: PatientRecordFetchErrors.NO_PATIENT_RECORD };
    }

    if (error) {
        return { data: null, error: PatientRecordFetchErrors.FETCH_FAILED };
    }

    // sort transcription sections by created_at ascending to get correct chronological order
    const patientRecord = data?.[0] as unknown as PatientRecord;
    if (patientRecord.transcriptions) {
        if ((patientRecord.transcriptions as any).transcript_sections) {
            (patientRecord.transcriptions as any).transcript_sections.sort(
                (a: TranscriptSection, b: TranscriptSection) => {
                    return new Date(a.created_at as string).getTime() - new Date(b.created_at as string).getTime();
                },
            );
        }
    }

    if (data && data[0] && data[0].chat_completions && data[0].chat_completions.length) {
        // Force one-to-one relationship of document_exports to document_type
        for (let i = 0; i < data[0].chat_completions.length; i++) {
            const documentType = data[0].chat_completions[i].document_types;
            if (documentType && Array.isArray(documentType.document_exports)) {
                patientRecord.chat_completions![i].document_types!.document_exports =
                    (documentType.document_exports[0] as DocumentExport) || null;
            }
        }

        // Always filter out INSIGHTS documents — they are never shown on the visit view
        // (billing-isolated scoring docs; not user-facing regardless of impersonation or insights flags).
        patientRecord.chat_completions = patientRecord.chat_completions!.filter(
            (completion) => !isInsightsDocument(completion.document_types),
        );
    }

    return { data: patientRecord, error: null };
};

export const fetchPatientVisits = async (
    patientId: string,
    accountID?: string,
    abortSignal?: AbortSignal,
): Promise<{ data: VisitHeader[]; error: Error | null }> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for fetchPatientVisits");
        return { data: [], error: new Error("No effective session found") };
    }

    const storedAccountId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(
        accountID || storedAccountId || effectiveSession.userId,
        effectiveSession,
    );

    if (!effectiveAccountID) {
        return { data: [], error: new Error("No account ID available") };
    }

    // Resolve clinic account id for multi-clinic team members (auth user id ≠ clinic account id)
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(abortSignal ?? new AbortController().signal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return { data: [], error: new Error("Account access denied") };
        }
        throw error;
    }
    const queryAccountId = accountData?.id ?? effectiveAccountID;

    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("patient_record")
            .select(
                `
			id,
			scheduled_at,
			appointment_duration,
			is_history,
			is_archived,
			type,
			chat_completions (
				created_at,
				is_archived,
				document_types (
					custom_document_type
				)
			)
		`,
            )
            .eq("patient_id_new", patientId)
            .eq("is_archived", false)
            .eq("account_id", queryAccountId);
    });

    if (error) {
        return { data: [], error: error };
    }

    if (!data || data.length === 0) {
        return { data: [], error: null };
    }

    const returnData: VisitHeader[] = data.map((visit) => {
        // set the updated at time to the most recent, not-archived, non-INSIGHTS chat completion
        // created_at date and otherwise null. INSIGHTS documents are internal analytics rows and
        // must never surface as the visit's "updated at" (see isInsightsDocument()).
        let updatedAt = null;
        if (visit.chat_completions) {
            updatedAt =
                visit.chat_completions
                    .filter((completion) => !isInsightsDocument(completion.document_types))
                    .find((completion) => !completion.is_archived)?.created_at || null;
        }

        // if it is a history visit, set the name to history
        // otherwise, set the name to the type if we have it and visit otherwise
        const displayName = visit.is_history ? "History" : visit.type ? visit.type : "Visit";

        return {
            id: visit.id,
            scheduledAt: visit.scheduled_at,
            updatedAt: updatedAt,
            displayName: displayName,
            isHistory: visit.is_history,
            appointmentDuration: visit.appointment_duration,
        };
    });
    return { data: returnData, error: null };
};

export const PATIENT_RECORDS_SELECT = `
    id,
    appointment_duration,
    scheduled_at,
    patient_name,
    patient_id,
    patient_id_new,
    created_at,
    status,
    stage,
    pims_type_color,
    linked_by_integration,
    resource_id,
    excluded_resource_id,
    doctors (
      id,
      doctor_name
    ),
    chat_completions (
      id,
      document_types (
        custom_document_type
      )
    ),
    document_assets (
      id
    ),
    transcriptions (
      id,
      transcript_sections (
        id,
        unprocessed_filename,
        processed_filename,
        status,
        is_archived
      )
    ),
    contacts (
      id,
      first_name,
      last_name,
      primary_phone,
      phones
    )
`;

export const fetchPatientRecords = async (
    selectedDate: any,
    selectedDoctorIds: string[] | null,
    abortSignal: AbortSignal,
    userId?: string,
): Promise<{ data: PatientRecord[]; error: any | null }> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for fetchPatientRecords");
        return { data: [], error: new Error("No effective session found") };
    }

    const storedAccountId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(
        userId || storedAccountId || effectiveSession.userId,
        effectiveSession,
    );

    if (!effectiveAccountID) {
        return { data: [], error: new Error("No account ID available") };
    }

    // Resolve clinic account id for multi-clinic team members (auth user id ≠ clinic account id)
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(abortSignal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return { data: [], error: new Error("Account access denied") };
        }
        throw error;
    }
    const queryAccountId = accountData?.id ?? effectiveAccountID;

    // check selectedDate.startDate is a valid date
    if (selectedDate?.["startDate"] && !isDate(selectedDate?.["startDate"])) {
        return { data: [], error: "Invalid start date" };
    }

    // Format selected date to start at 00:00:00
    // Format selected date to start at 00:00:00 in local time
    const queryStartDate = new Date(selectedDate?.["startDate"]);
    queryStartDate.setHours(0, 0, 0, 0);
    const queryStartDateString = queryStartDate.toISOString();

    // Format selected date to end at 23:59:59 in local time
    let queryEndDate = new Date(selectedDate?.["startDate"]);
    if (selectedDate?.["endDate"] && isDate(selectedDate?.["endDate"])) {
        queryEndDate = new Date(selectedDate?.["endDate"]);
    }
    queryEndDate.setHours(23, 59, 59, 999);
    const queryEndDateString = queryEndDate.toISOString();

    // Start with base query
    //create query for patient records, with transcriptions that have the same patient record id

    let query = supabase
        .from("patient_record")
        .select(PATIENT_RECORDS_SELECT)
        .abortSignal(abortSignal)
        .order("scheduled_at", { ascending: true })
        .gte("scheduled_at", queryStartDateString)
        .lte("scheduled_at", queryEndDateString)
        .eq("chat_completions.is_archived", false)
        .eq("document_assets.is_archived", false)
        .eq("is_archived", false)
        .eq("transcriptions.transcript_sections.is_archived", false); // ignore archived records

    // If selectedDoctorIds is provided, add filter for doctors
    if (selectedDoctorIds && selectedDoctorIds.length > 0) {
        if (selectedDoctorIds.some((id) => id === "")) {
            // selectedDoctorId === '' means unassigned patient records, so add is null to the query
            query = query.or(
                `doctor_id.in.(${selectedDoctorIds.filter((id) => id !== "").join(",")}), doctor_id.is.null`,
            );
        } else {
            query = query.in("doctor_id", selectedDoctorIds);
        }
    }

    // Always filter by account_id using the resolved clinic account ID
    query = query.eq("account_id", queryAccountId);

    // Execute the query
    const { data, error } = await retryWrapper(async () => {
        return await query;
    });

    if (error) {
        return { data: [], error: "Error querying patient records list" };
    }

    const records = ((data as PatientRecord[]) || []).map(withoutInsightsDocuments);

    return { data: records, error: null };
};

// INSIGHTS documents are internal analytics rows (never user-facing) and must not count
// toward a visit's document count in the list/calendar views — see isInsightsDocument().
const withoutInsightsDocuments = (record: PatientRecord): PatientRecord => ({
    ...record,
    chat_completions: record.chat_completions
        ? record.chat_completions.filter((completion) => !isInsightsDocument(completion.document_types))
        : record.chat_completions,
});

/**
 * EN-1325: one board row, fetched with the same select and filters as the board's own list load
 * (fetchPatientRecords), for realtime "patient" broadcasts. Deliberately NOT fetchPatientRecord:
 * that is the full patient-view embed (every document's chat_json/document_snapshot, transcript
 * section data, ...) and ran in every open tab on every broadcast.
 */
export const fetchPatientTableRecord = async (
    patientRecordId: string,
): Promise<{ data: PatientRecord | null; error: any | null }> => {
    if (!patientRecordId || !isUUID(patientRecordId)) {
        return { data: null, error: PatientRecordFetchErrors.NO_PATIENT_RECORD };
    }

    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession || !effectiveSession.userId) {
        return { data: null, error: new Error("No effective session found") };
    }

    const storedAccountId = getStoredSelectedClinicAccountId(effectiveSession.userId);
    const effectiveAccountID = resolveDocumentScopeAccountId(
        storedAccountId || effectiveSession.userId,
        effectiveSession,
    );

    // Resolve clinic account id for multi-clinic team members (auth user id ≠ clinic account id)
    let accountData: Account | null = null;
    try {
        accountData = await fetchAccountData(new AbortController().signal, effectiveAccountID);
    } catch (error) {
        if (error instanceof AccountAccessDeniedError) {
            return { data: null, error: new Error("Account access denied") };
        }
        throw error;
    }
    const queryAccountId = accountData?.id ?? effectiveAccountID;

    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("patient_record")
            .select(PATIENT_RECORDS_SELECT)
            .eq("id", patientRecordId)
            .eq("account_id", queryAccountId)
            .eq("is_archived", false)
            .eq("chat_completions.is_archived", false)
            .eq("document_assets.is_archived", false)
            .eq("transcriptions.transcript_sections.is_archived", false);
    });

    if (error) {
        return { data: null, error: PatientRecordFetchErrors.FETCH_FAILED };
    }
    if (!data || data.length === 0) {
        return { data: null, error: PatientRecordFetchErrors.NO_PATIENT_RECORD };
    }

    return { data: withoutInsightsDocuments(data[0] as unknown as PatientRecord), error: null };
};

// EN-1088: a visit currently being recorded into (from any device) has recording_active_until
// set in the future. This filter matches the same "no lease, or lease already expired" predicate
// as the API-Server and iOS delete/archive paths - see "The mechanism, verified" in the EN-1088
// plan - so a delete issued mid-recording is refused instead of archiving a visit the phone is
// still writing to. A client that never sets a lease (recording_active_until.is.null) archives
// exactly as it did before this change - that fail-open behavior is deliberate, not a gap.
const RECORDING_LEASE_FILTER = "recording_active_until.is.null,recording_active_until.lt.now()";

/**
 * EN-1494: the clinic a patient_record stage update or delete is scoped to. Same resolution as
 * resolveDocumentAccountIdForWrite, but it also says whether the id is `verified` (the clinic row
 * came back from /account/resolved) or only the fallback (stored clinic or session user id). The
 * fallback can be the wrong account for a non-owner member, so a zero-row result on it proves
 * nothing about the visit. null when it can't be resolved at all (no session, access denied, an
 * impersonating admin on an unverified id, or the lookup threw). Admins bypass RLS, so the
 * `account_id` filter this feeds is the only gate keeping the write inside the vet's clinic.
 *
 * A failed or slow /account/resolved does not block the write: fetchAccountData returns null and the
 * write goes ahead on the fallback id, refused only on zero rows (reported to Sentry). Its 30s success
 * cache and in-flight sharing keep this to at most one lookup per write; deletePatientRecords does
 * one per batch.
 */
type PatientRecordWriteClinic = { accountId: string; verified: boolean };

const resolveClinicForPatientRecordWrite = async (): Promise<PatientRecordWriteClinic | null> => {
    try {
        const effectiveSession = await getEffectiveSession();
        if (!effectiveSession) return null;
        const selectedClinicId = getStoredSelectedClinicAccountId(effectiveSession.userId);
        const effectiveAccountID = resolveDocumentScopeAccountId(selectedClinicId ?? "", effectiveSession);
        if (!effectiveAccountID) return null;
        const accountData = await fetchAccountData(new AbortController().signal, effectiveAccountID);
        if (accountData?.id) return { accountId: accountData.id, verified: true };
        // An impersonating admin bypasses RLS, so this filter is the only gate, and the fallback id may
        // be a clinic the target has since left. Refuse rather than write to an unverified clinic.
        if (effectiveSession.isImpersonating) {
            Sentry.captureException(
                new Error("Visit write refused: impersonating with an unverified clinic (EN-1494)"),
                {
                    tags: { errorCategory: "auth" },
                    extra: { fallbackAccountId: effectiveAccountID },
                },
            );
            return null;
        }
        return { accountId: effectiveAccountID, verified: false };
    } catch (error) {
        // No clinic to scope to. Access denied and an abort are expected; anything else (storage, a
        // bug, a malformed answer) is reported, or a new failure would quietly stop every visit write.
        const expected =
            error instanceof AccountAccessDeniedError || (error instanceof DOMException && error.name === "AbortError");
        if (!expected) {
            Sentry.captureException(error, {
                tags: { errorCategory: classifyNetworkError(error) },
                extra: { where: "resolveClinicForPatientRecordWrite" },
            });
        }
        return null;
    }
};

/**
 * EN-1494 review: a visit write that matched zero rows on an unverified (fallback) clinic id is
 * reported as a failure, and the vet only sees a generic "failed" toast. Retrying hits the same
 * failed /account/resolved lookup, so tell support what actually went wrong.
 */
const reportUnverifiedClinicWrite = (
    visitWrite: "delete" | "stage" | "update",
    patientRecordId: string,
    accountId: string,
) => {
    Sentry.captureException(new Error("Visit write refused: the clinic could not be verified (EN-1494)"), {
        tags: { errorCategory: "auth", visitWrite },
        extra: { patientRecordId, fallbackAccountId: accountId },
    });
};

export const deletePatientRecord = async (
    id: string,
    options?: { force?: boolean },
): Promise<DeletePatientRecordOutcome> => {
    const clinic = await resolveClinicForPatientRecordWrite();
    // Without a clinic we cannot scope the write, so nothing is attempted. "errored", never
    // "notFound": the visit may well exist, and notFound would remove it from the UI.
    if (!clinic) {
        return "errored";
    }
    return await deletePatientRecordInClinic(id, clinic, options);
};

const deletePatientRecordInClinic = async (
    id: string,
    { accountId, verified }: PatientRecordWriteClinic,
    options?: { force?: boolean },
): Promise<DeletePatientRecordOutcome> => {
    const { data, error } = await retryWrapper(async () => {
        let query = supabase
            .from("patient_record")
            .update({ is_archived: true, archived_by: PatientRecordArchivedBy.User })
            .eq("id", id)
            .eq("account_id", accountId);

        // EN-1088: `force` is the escape hatch behind a second, explicit confirmation in the UI
        // (see PatientViewMenu) - it omits the lease filter so a visit refused above can still be
        // deleted deliberately. Never the default.
        if (!options?.force) {
            query = query.or(RECORDING_LEASE_FILTER);
        }

        return await query.select("id");
    });

    if (error) {
        return "errored";
    }

    // EN-1088: PostgREST reports a refused update (a live recording lease) as HTTP 200 with an
    // EMPTY ARRAY, not an error - so an empty result here means the delete did not happen, not
    // that it happened silently. Do not collapse this into "deleted".
    if (!data || data.length === 0) {
        const outcome = await classifyZeroRowDelete(id, accountId);
        // EN-1494: on an unverified (fallback) clinic id, "not there" may just mean "not under
        // this id" - a non-owner member's visit in their real clinic. notFound would make the
        // thunk drop a live visit from the store, the EN-1088 lie, so report errored instead.
        // A row the check did find is still a genuine lease refusal.
        if (outcome === "notFound" && !verified) {
            reportUnverifiedClinicWrite("delete", id, accountId);
            return "errored";
        }
        return outcome;
    }

    return "deleted";
};

/**
 * EN-1088: zero rows has two causes and the response can't tell them apart - either the lease
 * filter excluded the row (a live recording, the intended refusal) or the row was never matched
 * at all (already deleted from another device, wrong account, RLS-invisible, or a stale id from
 * a list the vet has had open a while). Reporting the second as "refused" tells the vet a visit
 * is being recorded when it isn't, which is exactly the kind of invented state EN-1088 exists to
 * remove. So re-query by id WITHOUT the lease filter to see which it was. This runs only on the
 * zero-row path, which is rare, so the ordinary delete still costs one round trip.
 */
const classifyZeroRowDelete = async (id: string, accountId: string): Promise<DeletePatientRecordOutcome> => {
    // Deliberately NOT `.maybeSingle()`. maybeSingle collapses "no such row" and "this request
    // never produced an answer" into the same `data: null`, and retryWrapper hands back
    // `{ data: null, error: null }` for an aborted fetch (retryWrapper.ts, the AbortError /
    // code "20" / PGRST116 branch) - so with maybeSingle an aborted classify was indistinguishable
    // from a confirmed absence and returned "notFound". That is not a cosmetic mislabel: the thunk
    // removes notFoundIds from the store (patientTableRecordsSlice) and PatientViewMenu navigates
    // away, so an aborted delete made the visit disappear from the UI while the row sat untouched
    // in the database - the same lie as the bug this ticket exists to fix, pointed the other way.
    //
    // A list select separates them: a genuine miss is `[]`, and only a swallowed/aborted request
    // is `null`.
    //
    // EN-1494: scoped to the same clinic as the update, so an id from another clinic is "not
    // there for this user" (notFound), while a leased visit in this clinic still reads "refused".
    const { data, error } = await retryWrapper(async () => {
        return await supabase.from("patient_record").select("id").eq("id", id).eq("account_id", accountId).limit(1);
    });

    // If the existence check itself fails we genuinely don't know. Fall back to "refused": it
    // keeps the visit in the list and offers the force path, which is recoverable. Guessing
    // "notFound" would remove a visit that may well still exist - the same fabricated certainty
    // this function was added to eliminate, just relocated.
    if (error) {
        return "refused";
    }

    // `null` means the request was swallowed, not that the row is gone. Same reasoning as the
    // error branch above: never turn "we could not tell" into "it is not there".
    if (data === null) {
        return "refused";
    }

    return data.length > 0 ? "refused" : "notFound";
};

export const deletePatientRecords = async (
    ids: string[],
    options?: { force?: boolean; expectedAccountId?: string },
): Promise<DeletePatientRecordsResult> => {
    const result: DeletePatientRecordsResult = {
        deletedIds: [],
        refusedIds: [],
        notFoundIds: [],
        erroredIds: [],
    };

    // EN-1494: one clinic resolution per batch. If it fails nothing is attempted and every id is
    // errored - never deleted or notFound, since we don't know what state those visits are in.
    const clinic = await resolveClinicForPatientRecordWrite();
    if (!clinic) {
        result.erroredIds.push(...ids);
        return result;
    }
    // EN-1494 QA: the ids came from a list showing expectedAccountId. If the clinic now resolves to
    // another one (membership removed, identity switched), they would all match nothing and read as
    // notFound, and the caller would drop live visits from the list. Attempt nothing instead.
    if (options?.expectedAccountId && clinic.accountId !== options.expectedAccountId) {
        Sentry.captureException(new Error("Visit delete refused: the clinic resolved to another clinic (EN-1494)"), {
            tags: { errorCategory: "auth" },
            extra: { expectedAccountId: options.expectedAccountId, resolvedAccountId: clinic.accountId },
        });
        result.erroredIds.push(...ids);
        result.clinicChanged = true;
        return result;
    }

    for (const id of ids) {
        // EN-1088: each id is attempted independently so a lease refusal (or a genuine error) on
        // one visit doesn't abandon the rest of the batch - see deletePatientRecord above for the
        // refused-vs-errored distinction.
        const outcome = await deletePatientRecordInClinic(id, clinic, options?.force ? { force: true } : undefined);

        if (outcome === "deleted") {
            result.deletedIds.push(id);
        } else if (outcome === "refused") {
            result.refusedIds.push(id);
        } else if (outcome === "notFound") {
            result.notFoundIds.push(id);
        } else {
            result.erroredIds.push(id);
        }
    }

    return result;
};

// CHAT COMPLETIONS
/**
 * Inserts a chat completion into the database.
 */
export const insertChatCompletion = async (params: {
    userId: any;
    documentType: any;
    completion: any;
    patientRecordId: any;
    transcriptionId: any;
    header: any;
}): Promise<ChatCompletions | null> => {
    const { userId, documentType, completion, patientRecordId, transcriptionId, header } = params;
    const newMessageId = uuidv4();

    const newChatCompletion: any = {
        id: newMessageId,
        account_id: userId,
        chat_json: {
            chat_text: completion,
            header: header,
        },
        transcription_id: transcriptionId,
        document_type_id: documentType?.id,
        type: "audio",
        created_at: new Date().toISOString(),
        role: "assistant",
        patient_record_id: patientRecordId,
        chat_json_original: {
            chat_text: completion,
            header: documentType?.document_type_name,
        },
        header: header,
        status: DocumentStatus.Processing,
        document_snapshot: documentType?.creator_prompt_components,
        is_archived: false,
    };

    const { error } = await retryWrapper(async () => {
        return await supabase.from("chat_completions").upsert(newChatCompletion);
    });

    if (error) {
        return null;
    }

    newChatCompletion.document_types = documentType;

    return newChatCompletion;
};

export const fetchChatCompletionById = async (
    id: string,
    accountId: string,
): Promise<{ data: ChatCompletions | null; error: unknown }> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("chat_completions")
            .select("*, document_types(*)")
            .eq("id", id)
            .eq("account_id", accountId)
            .maybeSingle();
    });

    // Distinguish fetch failure from "row not found" — callers in optimistic-rollback
    // paths must NOT treat a network/RLS error as "doc was archived elsewhere."
    if (error) {
        Sentry.captureException(error, { extra: { id, accountId, op: "fetchChatCompletionById" } });
        return { data: null, error };
    }
    return { data: (data as unknown as ChatCompletions) ?? null, error: null };
};

/** @deprecated EN-1253: chat_json writes go through saveDocumentContent (API-Server). */
export const upsertChatCompletions = async (
    chatCompletions: Partial<ChatCompletions>,
): Promise<{ error: string | null }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("chat_completions").upsert({ ...chatCompletions, is_edited: true } as any);
    });

    if (error) {
        return { error: error?.message || "Error upserting chat completions" };
    }

    return { error: null };
};

export const deleteChatCompletions = async (id: string): Promise<boolean> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("chat_completions").update({ is_archived: true }).eq("id", id);
    });

    if (error) {
        return false;
    }
    return true;
};

export const updateChatCompletions = async (
    chatCompletions: Partial<ChatCompletions>,
    id: string,
): Promise<{ error: string | null }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("chat_completions")
            .update({
                ...(chatCompletions as any),
            })
            .eq("id", id);
    });

    if (error) {
        //return error function to parent caller
        return { error: error?.message || "Error updating chat completions" };
    }
    return { error: null };
};

export const updateChatCompletionsBulk = async (
    chatCompletions: ChatCompletions[],
): Promise<{ error: string | null }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("chat_completions").upsert([...(chatCompletions as any)]);
    });

    if (error) {
        //return error function to parent caller
        return { error: error?.message || "Error updating chat completions" };
    }
    return { error: null };
};

export const getChatCompletionAssets = async (
    chatCompletionIds: string[],
): Promise<{ error: string | null; data: ChatCompletionAsset[] | null }> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("chat_completions_transcript_sections")
            .select("chat_completion_id, transcript_section_id, document_asset_id")
            .in("chat_completion_id", chatCompletionIds);
    });

    if (error) {
        return { error: error?.message || "Error getting chat completion assets", data: null };
    }

    return {
        error: null,
        data:
            (data as {
                chat_completion_id: string;
                transcript_section_id: string | null;
                document_asset_id: string | null;
            }[]) || null,
    };
};

// TRANSCRIPTIONS
export const upsertTranscription = async (transcript: Transcript): Promise<{ error: string | null }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("transcriptions").upsert({
            ...transcript,
        });
    });

    if (error?.code === "23503" && transcript?.patient_record_id) {
        //foreign key error something got hung on the upsertPatientRecord
        const { error: pError } = await retryWrapper(async () => {
            return await supabase.from("patient_record").upsert({
                id: transcript.patient_record_id,
            });
        });

        const { error: tError } = await retryWrapper(async () => {
            return await supabase.from("transcriptions").upsert({
                ...transcript,
            });
        });

        if (pError || tError) {
            return { error: pError?.message || tError?.message || "Error upserting transcription" };
        } else {
            return { error: null };
        }
    }
    if (error) {
        return { error: error?.message || "Error upserting transcription" };
    }

    return { error: null };
};

export const setPatientRecordStage = async (id: string, stage: string): Promise<boolean> => {
    // EN-1494: scoped to the resolved clinic; admins bypass RLS, so this filter is the only gate.
    const clinic = await resolveClinicForPatientRecordWrite();
    if (!clinic) {
        return false;
    }

    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("patient_record")
            .update({
                stage: stage,
            })
            .eq("id", id)
            .eq("account_id", clinic.accountId)
            .select("id");
    });

    if (error) {
        return false;
    }

    // PostgREST reports a zero-row update as success, but the stage was not written: the visit
    // is stale or was archived concurrently, or (on a fallback clinic id) the id was the wrong
    // account. A null (swallowed/aborted) answer is treated the same: we cannot claim the save.
    if (!data || data.length === 0) {
        if (!clinic.verified) reportUnverifiedClinicWrite("stage", id, clinic.accountId);
        return false;
    }

    return true;
};

export const fetchPreviousTranscripts = async (existingTranscripts: Transcript[], numberOfTranscripts: number = 10) => {
    // Fetch the three most recent transcripts
    const { data: transcriptData, error: transcriptError } = await supabase
        .from("transcriptions")
        .select(
            `
            id,
            created_at,
            patient_record_id (
                id,
                patient_name
            )`,
        )
        .not("id", "in", `(${existingTranscripts.map((t) => t?.id)})`)
        .order("created_at", { ascending: false })
        .limit(numberOfTranscripts);

    if (transcriptError) {
        Sentry.captureException(transcriptError);
        return { data: null, error: transcriptError };
    }

    // Fetch transcript sections for each of the above transcripts
    const { data: sectionsData, error: sectionsError } = await supabase
        .from("transcript_sections")
        .select(
            `
            transcription_id,
            data`,
        )
        .in(
            "transcription_id",
            transcriptData.map((t) => t.id),
        );

    if (sectionsError) {
        Sentry.captureException(sectionsError);
        return { data: null, error: sectionsError };
    }

    // Map the transcripts to their sections
    const newTranscripts = transcriptData.map((transcript: any) => {
        const sections = sectionsData
            .filter((section) => section.transcription_id === transcript.id)
            .map((section) => section.data)
            .join("\n");

        return {
            id: transcript.id,
            created_at: transcript.created_at,
            transcript: sections,
            name: transcript?.patient_record_id?.patient_name || "",
        };
    });

    return { data: newTranscripts, error: null };
};

// ACCOUNT DATA
// Exported for supabaseClient.ACCOUNT_SELECT.spec.ts. The account_settings(...) embed below
// is a string, so nothing type-checks its contents: dropping a column from it compiles, and
// every flag then flattens to `?? false` in production while the flag specs stay green
// because they feed the flattening helpers pre-formed rows. Same reason PATIENT_RECORD_SELECT
// is exported and pinned.
export const ACCOUNT_SELECT = `
			clinic_name,
			clinic_location,
			clinic_time_zone,
			id,
			is_self_serve,
			feature_flags,
			created_at,
			meta_data,
			api_key,
			current_pims,
			hidden_documents,
			trial_end_date,
			has_completed_profile,
			communications_enabled,
			status,
			clinic_phone_number,
			clinic_banner_image_url,
			phone_calls_enabled,
			preferred_provider,
			calendar_mode,
			account_settings(pins_enabled, account_users_enabled, insights_enabled, happy_bridge_beta, bridge_upgrade_offer, monitoring_enabled, happy_bridge_change_detection_enabled)
			`;

/** Partial row from GET /account/resolved; canonical for PIN / team flags when present. */
type ResolvedApiSettings = {
    pins_enabled?: boolean;
    account_users_enabled?: boolean;
    insights_enabled?: boolean;
    happy_bridge_beta?: boolean;
    bridge_upgrade_offer?: boolean;
    monitoring_enabled?: boolean;
    happy_bridge_change_detection_enabled?: boolean;
};

/**
 * Account payload from GET /account/resolved. May duplicate flags on `account`; normalizer prefers `settings`.
 */
type ResolvedApiAccount = Omit<
    Account,
    | "pins_enabled"
    | "account_users_enabled"
    | "insights_enabled"
    | "happy_bridge_beta"
    | "bridge_upgrade_offer"
    | "monitoring_enabled"
    | "happy_bridge_change_detection_enabled"
    | "viewer_is_account_owner"
> & {
    pins_enabled?: boolean;
    account_users_enabled?: boolean;
    insights_enabled?: boolean;
    // No happy_bridge_beta, bridge_upgrade_offer, monitoring_enabled, or
    // happy_bridge_change_detection_enabled: all four exist only on account_settings (migrations
    // 20260708120000, 20260922135955, 20260824170000, and the Cornerstone change-detection one),
    // so the account half of this payload can never carry them. Omitted rather than optional so a
    // future read from the wrong half is a type error.
};

type AccountResolvedApiEnvelope = {
    settings?: ResolvedApiSettings | null;
    viewer_is_account_owner?: boolean;
    viewerIsAccountOwner?: boolean;
};

/** Full JSON body from GET /account/resolved; shared by fetchAccountData and auth flows. */
export type AccountResolvedHttpPayload = AccountResolvedApiEnvelope & {
    account: ResolvedApiAccount | null;
    preference_applied?: boolean;
    no_access_reason?: string;
};

function resolvePinsFromResolvedResponse(
    settings: ResolvedApiSettings | null | undefined,
    account: {
        pins_enabled?: boolean;
        account_users_enabled?: boolean;
        insights_enabled?: boolean;
    },
): {
    pins_enabled: boolean;
    account_users_enabled: boolean;
    insights_enabled: boolean;
    happy_bridge_beta: boolean;
    bridge_upgrade_offer: boolean;
    monitoring_enabled: boolean;
    happy_bridge_change_detection_enabled: boolean;
} {
    if (settings != null) {
        return {
            pins_enabled: settings.pins_enabled ?? false,
            account_users_enabled: settings.account_users_enabled ?? false,
            insights_enabled: settings.insights_enabled ?? false,
            happy_bridge_beta: settings.happy_bridge_beta ?? false,
            bridge_upgrade_offer: settings.bridge_upgrade_offer ?? false,
            monitoring_enabled: settings.monitoring_enabled ?? false,
            happy_bridge_change_detection_enabled: settings.happy_bridge_change_detection_enabled ?? false,
        };
    }
    return {
        pins_enabled: typeof account.pins_enabled === "boolean" ? account.pins_enabled : false,
        account_users_enabled:
            typeof account.account_users_enabled === "boolean" ? account.account_users_enabled : false,
        insights_enabled: typeof account.insights_enabled === "boolean" ? account.insights_enabled : false,
        // account_settings is the only home for these flags, so there's nothing to fall back to.
        // The three above are mirrored on the account row by older payloads; these four never were.
        happy_bridge_beta: false,
        bridge_upgrade_offer: false,
        monitoring_enabled: false,
        happy_bridge_change_detection_enabled: false,
    };
}

async function resolveViewerIsAccountOwner(
    accountId: string | null | undefined,
    authUserId: string | undefined,
    rawViewer: unknown,
): Promise<boolean> {
    if (rawViewer === true || rawViewer === "true") {
        return true;
    }
    if (rawViewer === false || rawViewer === "false") {
        return false;
    }
    if (!accountId || !authUserId) {
        return false;
    }
    // Legacy 1:1 account shape where account.id === auth user id.
    if (String(accountId) === authUserId) {
        return true;
    }
    // Multi-clinic fallback when API omits viewer_is_account_owner.
    const { data, error } = await retryWrapper(
        async () =>
            await (supabase as any).rpc("user_is_account_owner", {
                p_account_id: accountId,
                p_user_id: authUserId,
            }),
    );
    if (error) {
        return false;
    }
    return data === true || data === "true";
}

async function normalizeAccountFromResolvedApi(
    account: ResolvedApiAccount,
    envelope: AccountResolvedApiEnvelope,
    authUserId?: string,
): Promise<Account> {
    // Stripped so the resolved settings below win over anything mirrored on the account row.
    // happy_bridge_beta, bridge_upgrade_offer, monitoring_enabled, and
    // happy_bridge_change_detection_enabled aren't in this list because ResolvedApiAccount
    // doesn't carry them: they only ever come from account_settings.
    const { pins_enabled: _pins, account_users_enabled: _au, insights_enabled: _ins, ...rest } = account;
    const {
        pins_enabled,
        account_users_enabled,
        insights_enabled,
        happy_bridge_beta,
        bridge_upgrade_offer,
        monitoring_enabled,
        happy_bridge_change_detection_enabled,
    } = resolvePinsFromResolvedResponse(envelope.settings, account);

    const rawViewer = envelope.viewer_is_account_owner ?? envelope.viewerIsAccountOwner;
    const viewer_is_account_owner = await resolveViewerIsAccountOwner(account.id, authUserId, rawViewer);

    return {
        ...rest,
        pins_enabled,
        account_users_enabled,
        insights_enabled,
        happy_bridge_beta,
        bridge_upgrade_offer,
        monitoring_enabled,
        happy_bridge_change_detection_enabled,
        viewer_is_account_owner,
    };
}

type AccountSettingsJoinRow = Omit<
    Account,
    | "pins_enabled"
    | "account_users_enabled"
    | "insights_enabled"
    | "happy_bridge_beta"
    | "bridge_upgrade_offer"
    | "monitoring_enabled"
    | "happy_bridge_change_detection_enabled"
    | "viewer_is_account_owner"
> & {
    account_settings: {
        pins_enabled?: boolean;
        account_users_enabled?: boolean;
        insights_enabled?: boolean;
        happy_bridge_beta?: boolean;
        bridge_upgrade_offer?: boolean;
        monitoring_enabled?: boolean;
        happy_bridge_change_detection_enabled?: boolean;
    } | null;
};

async function buildAccountFromAccountSettingsJoinRow(
    row: AccountSettingsJoinRow,
    authUserId: string | undefined,
): Promise<Account> {
    const { account_settings, ...accountRow } = row;
    const pins_enabled = account_settings?.pins_enabled ?? false;
    const account_users_enabled = account_settings?.account_users_enabled ?? false;
    const insights_enabled = account_settings?.insights_enabled ?? false;
    const happy_bridge_beta = account_settings?.happy_bridge_beta ?? false;
    const bridge_upgrade_offer = account_settings?.bridge_upgrade_offer ?? false;
    const monitoring_enabled = account_settings?.monitoring_enabled ?? false;
    const happy_bridge_change_detection_enabled = account_settings?.happy_bridge_change_detection_enabled ?? false;
    const viewer_is_account_owner = await resolveViewerIsAccountOwner(accountRow.id, authUserId, undefined);
    return {
        ...accountRow,
        pins_enabled,
        account_users_enabled,
        insights_enabled,
        happy_bridge_beta,
        bridge_upgrade_offer,
        monitoring_enabled,
        happy_bridge_change_detection_enabled,
        viewer_is_account_owner,
    };
}

/** Session has no clinic access (e.g. team sign-in disabled for the account). */
export class AccountAccessDeniedError extends Error {
    readonly reason: string;
    constructor(reason: string) {
        super("Account access denied");
        // tsconfig targets ES5, where a subclass of Error loses its prototype and `instanceof` is
        // always false. Vite ignores that target, so prod was fine; Jest was not, which left the
        // access-denied paths untested (EN-1494 review).
        Object.setPrototypeOf(this, AccountAccessDeniedError.prototype);
        this.name = "AccountAccessDeniedError";
        this.reason = reason;
    }
}

// Short-lived cache for successful fetchAccountData resolutions only. Do not cache null on
// errors — transient network/API failures would otherwise block all callers until TTL expires.
// Cache key includes the send-as identity (see getSendAsIdentity), the userId and the selected clinic
// account ID to handle impersonation and multi-clinic scenarios.
const accountDataCache = new Map<string, { data: Account; timestamp: number }>();
const ACCOUNT_CACHE_TTL_MS = 30000; // 30 seconds

/**
 * EN-1313: the identity apiFetch would send a request as if it read the session right now. apiFetch
 * reads the impersonation session from localStorage itself, later, at send time, so callers that need
 * the identity actually sent as must re-check (see fetchAccountDataViaResolvedApi). It can disagree
 * with the userId a caller passes (a tab that loads mid-impersonation asks about the admin before its
 * React state knows; another tab can stop impersonation under it). The server answers for the send-as identity, so the cache must be
 * keyed by it too, or one identity's row is served to the other for the whole TTL.
 */
function getSendAsIdentity(): string {
    try {
        const raw = localStorage.getItem("impersonation_session");
        const session = raw ? (JSON.parse(raw) as { isImpersonating?: boolean; targetUserId?: string }) : null;
        return session?.isImpersonating && session.targetUserId ? `as:${session.targetUserId}` : "self";
    } catch {
        return "self";
    }
}

function getAccountCacheKey(userId: string | undefined, sendAs: string = getSendAsIdentity()): string | undefined {
    if (!userId) return undefined;
    const selectedClinicId = getStoredSelectedClinicAccountId(userId);
    const scope = selectedClinicId ? `${userId}:${selectedClinicId}` : userId;
    return `${sendAs}|${scope}`;
}

function getCachedAccountData(userId: string | undefined): Account | undefined {
    const cacheKey = getAccountCacheKey(userId);
    if (!cacheKey) return undefined;
    const cached = accountDataCache.get(cacheKey);
    if (!cached) return undefined;
    const now = Date.now();
    if (now - cached.timestamp > ACCOUNT_CACHE_TTL_MS) {
        accountDataCache.delete(cacheKey);
        return undefined;
    }
    return cached.data;
}

function setCachedAccountData(userId: string | undefined, data: Account, sendAs?: string): void {
    const cacheKey = getAccountCacheKey(userId, sendAs);
    if (!cacheKey) return;
    accountDataCache.set(cacheKey, { data, timestamp: Date.now() });
}

function awaitWithAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
    if (signal.aborted) {
        return Promise.reject(new DOMException("Aborted", "AbortError"));
    }
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => {
            signal.removeEventListener("abort", onAbort);
            reject(new DOMException("Aborted", "AbortError"));
        };
        signal.addEventListener("abort", onAbort);
        promise
            .then((value) => {
                signal.removeEventListener("abort", onAbort);
                resolve(value);
            })
            .catch((err: unknown) => {
                signal.removeEventListener("abort", onAbort);
                reject(err);
            });
    });
}

/** One in-flight GET account/resolved per account cache key; HTTP layer ignores per-caller abort (see awaitWithAbort). */
const accountResolvedHttpInflight = new Map<string, Promise<AccountResolvedHttpPayload>>();

function getOrCreateAccountResolvedHttpPromise(userId: string): Promise<AccountResolvedHttpPayload> {
    const key = getAccountCacheKey(userId);
    if (!key) {
        return Promise.reject(new Error("getOrCreateAccountResolvedHttpPromise: missing account cache key"));
    }
    const existing = accountResolvedHttpInflight.get(key);
    if (existing) {
        return existing;
    }
    const run = (async () => {
        const { apiFetch } = await import("@common/lib/api");
        const body = await apiFetch<AccountResolvedHttpPayload>("account/resolved", {
            method: "GET",
        });
        if (body === undefined) {
            throw new Error("GET account/resolved returned empty body");
        }
        return body;
    })();
    accountResolvedHttpInflight.set(key, run);
    void run.finally(() => {
        if (accountResolvedHttpInflight.get(key) === run) {
            accountResolvedHttpInflight.delete(key);
        }
    });
    return run;
}

/**
 * GET account/resolved for login / select-clinic — coalesced with fetchAccountData (single HTTP request per cache key).
 * Returns null on failure. Re-throws AbortError when `abortSignal` aborts.
 */
export async function fetchAccountResolvedEnvelope(
    userId: string,
    abortSignal?: AbortSignal,
): Promise<AccountResolvedHttpPayload | null> {
    try {
        const p = getOrCreateAccountResolvedHttpPromise(userId);
        const data = abortSignal ? await awaitWithAbort(p, abortSignal) : await p;
        return data;
    } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
            throw error;
        }
        return null;
    }
}

/** Coalesce concurrent fetchAccountData(account/resolved path) for the same user + clinic (parallel page load, refetch). */
const accountDataInflight = new Map<string, Promise<Account | null>>();

function getAccountInflightKey(userId: string | undefined): string | undefined {
    return getAccountCacheKey(userId);
}

async function fetchAccountDataViaResolvedApi(abortSignal: AbortSignal, userId: string): Promise<Account | null> {
    // EN-1313: capture the send-as identity now, when the request is made (the same moment its in-flight
    // coalescing key is computed). Re-reading it after the awaits below would file the response under
    // whatever identity is current when it lands, so a stop in another tab mid-request would cache the
    // target's row under the admin's key. apiFetch reads the session a few awaits later (after
    // getSession, which can refresh the token), so the writes below are skipped if the identity at write
    // time differs from this one. A change and change back within one request is not caught; closing that
    // needs apiFetch to report the identity it sent as.
    const sendAs = getSendAsIdentity();
    try {
        const data = await awaitWithAbort(getOrCreateAccountResolvedHttpPromise(userId), abortSignal);
        const { getEffectiveSession } = await import("@common/lib/api");
        const { clearStoredSelectedClinicAccountId } = await import("@common/lib/selectedClinicAccountStorage");
        if (data?.preference_applied === false) {
            const effectiveSession = await getEffectiveSession();
            // EN-1313: storageUserId is the identity current now. If that is no longer the one this
            // request was captured for, clearing its selection would wipe the other identity's clinic.
            if (getSendAsIdentity() === sendAs) {
                const storageUserId = effectiveSession?.userId || userId;
                // Invalidate old cache entry before clearing clinic selection
                const oldCacheKey = getAccountCacheKey(storageUserId, sendAs);
                if (oldCacheKey) {
                    accountDataCache.delete(oldCacheKey);
                }
                clearStoredSelectedClinicAccountId(storageUserId);
            }
        }
        if (!data?.account) {
            if (data?.no_access_reason === "account_users_disabled") {
                throw new AccountAccessDeniedError("account_users_disabled");
            }
            return null;
        }

        // userId here can be an impersonation target. For ownership fallback, always use
        // the actual authenticated Supabase user id (admin/operator), not the effective user.
        const {
            data: { user },
        } = await supabase.auth.getUser();
        const result = await normalizeAccountFromResolvedApi(data.account, data, user?.id);
        if (getSendAsIdentity() === sendAs) {
            setCachedAccountData(userId, result, sendAs);
        }
        return result;
    } catch (error) {
        // Re-throw AbortError so caller's abort guard works correctly
        if (error instanceof DOMException && error.name === "AbortError") {
            throw error;
        }
        if (error instanceof AccountAccessDeniedError) {
            throw error;
        }
        return null;
    }
}

export const fetchAccountData = async (
    abortSignal: AbortSignal,
    userId?: string,
    skipCache: boolean = false,
): Promise<Account | null> => {
    if (abortSignal.aborted) {
        throw new DOMException("Aborted", "AbortError");
    }

    // Check cache first unless caller explicitly requests fresh data
    if (!skipCache) {
        const cachedData = getCachedAccountData(userId);
        if (cachedData !== undefined) {
            return cachedData;
        }
    }
    if (userId) {
        // Resolved account + settings come from GET /account/resolved (API server), not PostgREST.
        const inflightKey = getAccountInflightKey(userId);
        if (inflightKey) {
            const existing = accountDataInflight.get(inflightKey);
            if (existing) {
                return existing;
            }
        }

        const run = fetchAccountDataViaResolvedApi(abortSignal, userId);
        if (inflightKey) {
            accountDataInflight.set(inflightKey, run);
            void run
                .finally(() => {
                    if (accountDataInflight.get(inflightKey) === run) {
                        accountDataInflight.delete(inflightKey);
                    }
                })
                .catch(() => {});
        }
        return run;
    }

    const { data, error } = await retryWrapper(async () => {
        return await supabase.from("account").select(ACCOUNT_SELECT).abortSignal(abortSignal).limit(1).single();
    });

    if (error) {
        return null;
    }

    const row = data as AccountSettingsJoinRow | null;
    if (!row) {
        return null;
    }

    const {
        data: { user },
    } = await supabase.auth.getUser();
    const result = await buildAccountFromAccountSettingsJoinRow(row, user?.id);
    setCachedAccountData(userId, result);
    return result;
};

// COMMUNICATIONS DATA
export const fetchCommunicationsData = async (
    abortSignal: AbortSignal,
    userId?: string,
): Promise<Communications[] | null> => {
    const { data, error } = await retryWrapper(async () => {
        let query = supabase
            .from("communications")
            .select(
                `
			id,
			account_id,
			communication_type,
			value,
			is_active,
			is_primary,
			confirmed_at,
			confirmation_sent_at,
			confirmation_token,
			signature,
			signature_json,
			cc_active,
			created_at,
			updated_at
			`,
            )
            .abortSignal(abortSignal);

        // If userId is provided (for impersonation), filter by that user
        if (userId) {
            query = query.eq("account_id", userId);
        }

        return await query.order("created_at", { ascending: true });
    });

    if (error) {
        return null;
    }

    return data as Communications[] | null;
};

export const upsertCommunicationsData = async (
    communicationsData: Partial<Communications>,
): Promise<Communications | null> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for upsertCommunicationsData");
        return null;
    }

    if (!communicationsData.account_id || !communicationsData.communication_type || !communicationsData.value) {
        console.error("Missing required fields for upsertCommunicationsData");
        return null;
    }

    const upsertData = communicationsData as Database["public"]["Tables"]["communications"]["Insert"];
    const { data, error } = await retryWrapper(async () => {
        return await supabase.from("communications").upsert(upsertData).select().single();
    });

    if (error) {
        return null;
    }

    return data as Communications | null;
};

export const updateCommunications = async (communication: Partial<Communications>): Promise<Communications | null> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for updateCommunications");
        return null;
    }

    if (!communication.account_id || !communication.id) {
        return null;
    }

    const updateData = communication as Database["public"]["Tables"]["communications"]["Update"];
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("communications")
            .update(updateData)
            .eq("id", communication.id as string)
            .select()
            .single();
    });

    if (error) {
        return null;
    }

    return data as Communications | null;
};

export const deleteCommunications = async (communicationId: string): Promise<boolean> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for deleteCommunications");
        return false;
    }

    const { error } = await retryWrapper(async () => {
        return await supabase.from("communications").delete().eq("id", communicationId);
    });

    if (error) {
        return false;
    }

    return true;
};

export const getDefaultView = async (userId: string, clinicAccountId?: string | null): Promise<VisitPage> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for getDefaultView");
        return VisitPage.List;
    }

    // Use the effective user ID from impersonation session, or fall back to the passed userId
    const effectiveUserId = effectiveSession.userId || userId;

    // For multi-clinic users, use the clinic account ID if provided; otherwise fall back to effective user ID
    const accountIdToQuery = clinicAccountId || effectiveUserId;
    if (!accountIdToQuery) {
        return VisitPage.List;
    }

    const { data, error } = await retryWrapper<{ meta_data: { default_view: VisitPage } }>(async () => {
        return await supabase.from("account").select("meta_data").eq("id", accountIdToQuery).single();
    });

    if (error) {
        return VisitPage.List;
    }

    return data?.meta_data?.default_view || VisitPage.List;
};

export const getMetaData = async (userId: string): Promise<AccountMetaData | null> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for getMetaData");
        return null;
    }

    // Use the effective user ID from impersonation session, or fall back to the passed userId
    const effectiveUserId = effectiveSession.userId || userId;

    const { data, error } = await retryWrapper<{ meta_data: AccountMetaData }>(async () => {
        return await supabase.from("account").select("meta_data").eq("id", effectiveUserId).single();
    });

    if (error) {
        return null;
    }

    return data?.meta_data || null;
};

export const fetchHiddenDocumentsFromAccount = async (abortSignal: AbortSignal, userId?: string): Promise<string[]> => {
    const { data, error } = await retryWrapper<{ hidden_documents: string[] }>(async () => {
        let query = supabase.from("account").select("hidden_documents").abortSignal(abortSignal);

        // If userId is provided (for impersonation), filter by that user
        if (userId) {
            query = query.eq("id", userId);
        }

        return await query.limit(1).maybeSingle();
    });

    if (error) {
        return [];
    }

    return (data?.hidden_documents as string[]) || [];
};

// autosaved
export const updateAccountData = async ({
    id,
    clinic_name,
    clinic_location,
    clinic_time_zone,
    has_completed_profile,
    current_pims,
    clinic_banner_image_url,
    clinic_phone_number,
}: Partial<Account>) => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for updateAccountData");
        return;
    }

    const { error } = await supabase
        .from("account")
        .update({
            clinic_name,
            clinic_location,
            clinic_time_zone: clinic_time_zone as Database["public"]["Enums"]["time_zone_old"],
            has_completed_profile,
            current_pims: current_pims as Database["public"]["Enums"]["pims"],
            clinic_banner_image_url,
            clinic_phone_number,
        })
        .eq("id", id as string);

    if (error) {
        Sentry.captureException(error);
    }
};

export const upsertAddress = async (address: Address): Promise<boolean> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        console.error("No effective session found for upsertAddress");
        return false;
    }

    const { error } = await supabase.from("address").upsert({ ...address });

    if (error) {
        Sentry.captureException(error);
        return false;
    }

    return true;
};

export const updateAccountHiddenDocs = async ({ id, hidden_documents }: any): Promise<{ error: null | string }> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        return { error: "No effective session found" };
    }

    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("account")
            .update({
                hidden_documents: hidden_documents,
            })
            .eq("id", id);
    });

    if (error) {
        return { error: error?.message || "Error updating hidden documents" };
    }

    return { error: null };
};

export const updateAccountPreferredProvider = async ({
    id,
    preferred_provider,
}: any): Promise<{ error: null | string }> => {
    // Get effective session for impersonation
    const effectiveSession = await getEffectiveSession();
    if (!effectiveSession) {
        return { error: "No effective session found" };
    }

    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("account")
            .update({
                preferred_provider: preferred_provider,
            })
            .eq("id", id);
    });

    if (error) {
        return { error: error?.message || "Error updating preferred provider" };
    }

    return { error: null };
};

//ACCOUNT_INTEGRATIONS
/** How long the strict integration re-check waits before reporting "couldn't tell". */
export const STRICT_INTEGRATION_READ_TIMEOUT_MS = 5_000;

/**
 * Integration rows for one account, for callers that must distinguish "couldn't ask" from "nothing
 * active". **Throws** on a query error.
 *
 * A deliberate sibling of `getAccountIntegrations` below rather than a flag on it: that one returns
 * `[]` when the query fails, and every existing caller depends on that forgiving behaviour. But `[]`
 * also reads as "no integration is active", so it cannot be used to decide whether an irreversible
 * switch landed — the bridge wizard needs to know the difference before it tells a clinic "Nothing
 * changed for your clinic" (see `BridgeSetupFlow`'s setup-failure re-check).
 *
 * Lives here, beside the other Supabase reads, rather than in `api/bridge.ts`: importing the
 * Supabase client into that module constructs a real client at import time for every consumer of it,
 * which broke `useBridgeSetup.spec` on env vars it never needed.
 */
export const getAccountIntegrationsStrict = async (accountId: string): Promise<AccountIntegration[]> => {
    // Bounded because the caller is on the critical path of a screen that is otherwise blank:
    // supabase-js applies no default timeout, and this read is awaited while a clinic waits to be told
    // what happened to their cutover. The abort surfaces as a rejection, which the caller already
    // treats as "couldn't tell" — the honest answer for a read that never came back.
    //
    // `AbortController` + `setTimeout` rather than `AbortSignal.timeout`, which does not exist on
    // Safari < 16 — reachable on the older exam-room iPads this app runs on, and absent under jest.
    // There it would throw a TypeError inside the caller's inner try, quietly turning every re-check
    // into "couldn't tell" on those devices. `AbortController` is what the rest of this codebase
    // already uses (see `AccountProvider`).
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), STRICT_INTEGRATION_READ_TIMEOUT_MS);
    try {
        const { data, error } = await supabase
            .from("account_integrations")
            .select(
                // Must stay identical to `getAccountIntegrations` below, column for column. This
                // function now also backs `AccountProvider`'s `pmsIntegrationData` (EN-1137), whose
                // consumers read far more than the bridge wizard's `is_active` /
                // `integrations.integration_name`: `Export.tsx` needs `export_enabled`, `id`,
                // `connected_third_party`, `last_request_status`, `practice_id`, `last_request` and
                // `api_url`. Every field on `AccountIntegration` is optional, so narrowing this list
                // does not fail typecheck — it silently returns rows whose missing `export_enabled`
                // reads as "export off" and hides the button fleet-wide. Asserted in
                // `getAccountIntegrationsStrict.spec.ts`.
                "integrations(integration_name), id, practice_id, connected_third_party, last_request, last_request_status, api_url, export_enabled, is_active, cornerstone_default_alert_color",
            )
            .eq("account_id", accountId)
            .abortSignal(controller.signal);
        if (error) throw error;
        return (data ?? []) as AccountIntegration[];
    } finally {
        clearTimeout(timer);
    }
};

export const getAccountIntegrations = async (
    abortSignal: AbortSignal,
    userId?: string,
): Promise<AccountIntegration[]> => {
    const { data, error } = await retryWrapper(async () => {
        let query = supabase
            .from("account_integrations")
            .select(
                "integrations(integration_name), id, practice_id, connected_third_party, last_request, last_request_status, api_url, export_enabled, is_active, cornerstone_default_alert_color",
            )
            .abortSignal(abortSignal);

        // If userId is provided (for impersonation), filter by that user
        if (userId) {
            query = query.eq("account_id", userId);
        }

        return await query;
    });

    if (error) {
        return [];
    }

    if (data && data?.length !== 0) {
        return data as AccountIntegration[];
    }

    return [];
};

export const updateExportEnabled = async (
    integrationId: string,
    exportEnabled: boolean,
): Promise<{ error: Error | null }> => {
    const { error } = await supabase
        .from("account_integrations")
        .update({ export_enabled: exportEnabled })
        .eq("id", integrationId);

    if (error) {
        return { error: new Error(error?.message || "Error updating export enabled") };
    }

    return { error: null };
};

export const updateCornerstoneDefaultAlertColor = async (
    integrationId: string,
    color: string | null,
): Promise<{ error: Error | null }> => {
    const { error } = await supabase
        .from("account_integrations")
        .update({ cornerstone_default_alert_color: color })
        .eq("id", integrationId);

    if (error) {
        return { error: new Error(error?.message || "Error updating default alert color") };
    }

    return { error: null };
};

//TRANSCRIPT SECTIONS
export const upsertTranscriptSection = async (
    // account_id is required (not optional) here, unlike on TranscriptSection itself: every caller
    // must explicitly decide its value rather than silently omitting the key. This only catches an
    // omitted key at compile time, NOT a resolved `undefined` (every real caller's fallback branch,
    // e.g. `accountData?.id` before the account context has loaded, type-checks cleanly) — the
    // runtime check below is the actual safety net for that case.
    transcriptSection: Omit<TranscriptSection, "origin" | "account_id"> & { account_id: string | undefined },
): Promise<{ error: null | string }> => {
    if (transcriptSection.account_id === undefined) {
        // EN-1171: an undefined account_id falls back to the transcript_sections.account_id column
        // default (auth.uid(), the individual login) instead of the shared clinic account, hiding
        // the row from every other user on the account. Flag it so a regression is visible in
        // monitoring instead of only surfacing as a support ticket — the write still proceeds
        // (unset here means "we couldn't resolve one", not "abort the upload").
        Sentry.captureException(new Error("upsertTranscriptSection called with unresolved account_id"), {
            extra: {
                transcriptSectionId: transcriptSection.id,
                transcriptionId: transcriptSection.transcription_id,
                patientRecordId: transcriptSection.patient_record_id,
            },
        });
    }

    const withOrigin: TranscriptSection = { ...transcriptSection, origin: window.navigator.userAgent };
    const { error } = await retryWrapper(async () => {
        return await supabase.from("transcript_sections").upsert({
            ...(withOrigin as any),
        });
    });

    if (error) {
        return { error: error?.message || "Error upserting transcription section" };
    }

    return { error: null };
};

// update the display name of a transcript section
export const updateTranscriptSectionDisplayName = async (
    transcriptSection: Pick<TranscriptSection, "id" | "display_name">,
): Promise<{ error: null | string }> => {
    if (!transcriptSection.id || typeof transcriptSection.display_name !== "string") {
        return { error: "Invalid transcript section id or display_name" };
    }

    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("transcript_sections")
            .update({ display_name: transcriptSection.display_name })
            .eq("id", transcriptSection.id as string);
    });

    if (error) {
        return { error: error?.message || "Error updating transcript section" };
    }

    return { error: null };
};

// update the display name of a document asset
export const updateDocumentAssetDisplayName = async (
    documentAsset: Pick<DocumentAsset, "id" | "file_name">,
): Promise<{ error: null | string }> => {
    if (!documentAsset.id || typeof documentAsset.file_name !== "string") {
        return { error: "Invalid document asset id or file_name" };
    }

    const { error } = await retryWrapper(async () => {
        return await supabase
            .from("document_assets")
            .update({ file_name: documentAsset.file_name })
            .eq("id", documentAsset.id as string);
    });

    if (error) {
        return { error: error?.message || "Error updating asset name" };
    }

    return { error: null };
};

// delete a document asset from the db
export const deleteDocumentAsset = async (documentAssetId: string): Promise<{ error: null | string }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("document_assets").update({ is_archived: true }).eq("id", documentAssetId);
    });

    if (error) {
        return { error: error?.message || "Error deleting document asset" };
    }

    return { error: null };
};

export const fetchAllTranscriptSections = async (
    transcriptId: string,
): Promise<{ error: null | string; data: TranscriptSection[] }> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("transcript_sections")
            .select(
                `id,
				transcription_id,
				data,
				unprocessed_filename,
				was_removed,
				processed_filename,
				is_archived
        `,
            )
            .eq("transcription_id", transcriptId)
            .eq("is_archived", false)
            .order("created_at", { ascending: true });
    });

    if (error) {
        return { error: error?.message || "Error Fetching Transcript Sections", data: [] };
    }

    return { error: null, data: (data as TranscriptSection[]) || [] };
};

export const deleteTranscriptSection = async (transcriptSectionId: string): Promise<{ error: null | string }> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("transcript_sections").update({ is_archived: true }).eq("id", transcriptSectionId);
    });

    if (error) {
        return { error: error?.message || "Error Deleting Transcript Sections" };
    }
    return { error: null };
};

export const deleteAllTranscriptSections = async (
    transcriptSectionIds: string[],
): Promise<{ error: null | string }> => {
    for (let i = 0; i < transcriptSectionIds.length; i++) {
        const { error } = await deleteTranscriptSection(transcriptSectionIds[i]);
        if (error) {
            return { error: error || "Error Deleting Transcript Sections" };
        }
    }
    return { error: null };
};

//STORAGE API
export const isAudioFileInSupabase = async (accountId: string, transcriptionId: string, audioName: string) => {
    // EN-1266: check the one object by its exact path. `list()` + `search` applies the SELECT
    // policy to every audio-storage object before the prefix filter (millions in prod), so it
    // times out; a single-object HEAD goes through the policy's transcriptions primary-key lookup
    // once. The basename search also matched prefixes ("clip" matched "clip2.mp4").
    //
    // Upstream reports of `exists is not a function` in browser bundles come from supabase-js
    // releases that bundled a storage-js older than exists(). We pin supabase-js 2.83.0, which pins
    // storage-js 2.83.0, and exists() is in the ESM build Vite uses
    // (storage-js dist/module/packages/StorageFileApi.js). If it ever went missing, the call throws
    // into the catch below: every call reports to Sentry and returns false, and the caller just
    // re-uploads to the same path. Nothing gates playback on this.
    try {
        const { data, error } = await supabase.storage
            .from("audio-storage")
            .exists(`${accountId}/${transcriptionId}/${audioName}`);
        // storage-js returns { data: false, error } only for a HEAD 400/404. Storage answers 400 not
        // just for a missing object but also for an RLS denial or an expired/anonymous session, and
        // HEAD has no body, so those can't be told apart here. false is still safe: the only caller
        // re-uploads with upsert to this same path. 401/403/5xx/network errors throw into the catch.
        if (error) {
            Sentry.addBreadcrumb({
                category: "storage",
                level: "info",
                message: "isAudioFileInSupabase: exists() returned false with an error (400/404)",
                data: { status: (error as any)?.originalError?.status ?? (error as any)?.status },
            });
            return false;
        }
        return data === true;
    } catch (error) {
        Sentry.captureException(error, {
            tags: { errorCategory: classifyNetworkError(error) },
            extra: { status: (error as any)?.originalError?.status ?? (error as any)?.status },
        });
        return false;
    }
};

export const uploadAudioFileToSupabase = async (
    audioFile: Blob,
    audioName: string,
    tId: string,
    accountId: string | null | undefined,
): Promise<{ error: boolean; is409: boolean }> => {
    try {
        // Keyed by the clinic account (EN-1216), not the uploader's own login -- refuse rather
        // than write/check a "null/..." or "undefined/..." path that would be permanently
        // orphaned or falsely report a collision.
        if (!accountId) {
            Sentry.captureException(new Error("uploadAudioFileToSupabase called without a resolved account_id"), {
                extra: { transcriptionId: tId, audioName },
            });
            return { error: true, is409: false };
        }

        // check if audio already exists, return 409 if it does
        const isAudioInSupabase = await isAudioFileInSupabase(accountId, tId, audioName);
        if (isAudioInSupabase) {
            return { error: false, is409: true };
        }

        const { error } = await uploadToStorage("audio-storage", tId, audioName, audioFile, accountId);
        if (error) {
            return { error: true, is409: false };
        }

        return { error: false, is409: false };
    } catch (error) {
        Sentry.captureException(error);
        return { error: true, is409: false };
    }
};

export const uploadDocumentTemplatePdf = async (
    pdfFile: File,
    filename: string,
): Promise<{ error: Error | null; path: string | null; filename: string | null }> => {
    // Generate timestamp and sanitize filename
    const timestamp = Date.now();
    const sanitizedFilename = `${filename.replace(/\.pdf$/i, "").replace(/[^a-zA-Z0-9-_]/g, "_")}-${timestamp}.pdf`;
    const path = "document-template-upload";

    const { error, data } = await uploadToStorage("documents", path, sanitizedFilename, pdfFile);
    if (error) {
        return { error: error, path: null, filename: null };
    }

    return { error: null, path: data.path, filename: sanitizedFilename };
};

export const uploadToStorage = async (
    bucket: string,
    path: string,
    filename: string,
    file: Blob,
    // Overrides the effectiveUserId-derived path prefix below -- used by callers that must key by
    // the clinic account_id, not the uploader's own login: audio storage (EN-1216) and the
    // practice logo (EN-1477). Every other caller (document PDFs, CSV imports)
    // omits this and keeps the existing effectiveUserId-based path unchanged.
    pathPrefixOverride?: string,
): Promise<SupabaseResponse<{ path: string }>> => {
    try {
        let pathPrefix = pathPrefixOverride;

        if (!pathPrefix) {
            // Get effective session for impersonation
            const effectiveSession = await getEffectiveSession();
            if (!effectiveSession) {
                return { error: new Error("No effective session found for uploadToStorage"), data: null };
            }

            pathPrefix = effectiveSession.userId;
            if (!pathPrefix) {
                return { error: new Error("No effective user ID found for uploadToStorage"), data: null };
            }
        }

        const uploadPath = pathJoin(pathPrefix, path, filename);

        const { error } = await supabase.storage.from(bucket).upload(uploadPath, file, {
            upsert: true,
        });

        if (error) {
            // if error is 401 or 403, refresh session and try again
            if (error instanceof StorageApiError && (error.status === 401 || error.status === 403)) {
                const { error: refreshError } = await supabase.auth.refreshSession();
                if (refreshError) {
                    const errorForSentry = createErrorFromSupabaseError(refreshError);
                    Sentry.captureException(errorForSentry);
                    return { error: new Error("Failed to refresh session"), data: null };
                } else {
                    const { error: retryError }: any = await supabase.storage.from(bucket).upload(uploadPath, file, {
                        upsert: true,
                    });

                    if (retryError) {
                        Sentry.captureException(retryError, {
                            extra: { filename, uploadPath, pathPrefix },
                        });
                        return { error: new Error("Failed to upload file"), data: null };
                    }
                }
            } else {
                Sentry.captureException(error, {
                    extra: { filename, uploadPath, pathPrefix },
                });
                return { error: new Error("Failed to upload file"), data: null };
            }
        }

        return { error: null, data: { path: uploadPath } };
    } catch (error) {
        Sentry.captureException(error);
        return { error: new Error("Failed to upload file"), data: null };
    }
};

export const downloadFromStorage = async (bucket: string, path: string): Promise<SupabaseResponse<Blob>> => {
    const { data, error } = await supabase.storage.from(bucket).download(path);

    if (error) {
        Sentry.captureException(error);
        return { data: null, error: error };
    }

    return { data: data, error: null };
};

export const removeFromStorage = async (bucket: string, path: string): Promise<SupabaseResponse<boolean>> => {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) {
        Sentry.captureException(error);
        return { error: error, data: null };
    }

    return { error: null, data: true };
};

export const createBlobUrl = async (bucket: string, path: string): Promise<string | null> => {
    // create a signed url for the blob that expires in 1 day
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 60 * 60 * 24);

    if (error) {
        Sentry.captureException(error);
        return null;
    }

    return data.signedUrl;
};

export const uploadAndTranscribeBulkAudio = async (
    transcriptionId: string,
    transcriptSectionId: string,
    audioData: Blob,
    audioName: string,
    patientRecordId?: string | null,
    patientName: string = "",
    doctorId: string = "",
    documentIds: string[] = [],
    targetMonitoringDocumentId?: string | null,
    accountId?: string | null,
): Promise<{ error: boolean | null }> => {
    try {
        const { error, is409 } = await uploadAudioFileToSupabase(audioData, audioName, transcriptionId, accountId);
        //If Error, nothing we can do really.
        if (error && !is409) {
            return { error: true };
        }

        await upsertTranscriptSection({
            id: transcriptSectionId,
            transcription_id: transcriptionId,
            data: "",
            status: TranscriptSectionStatus.Uploaded,
            patient_record_id: patientRecordId || "",
            account_id: accountId ?? undefined,
        });

        const { error: transcribeError } = await transcribeAudio(
            transcriptionId,
            transcriptSectionId,
            audioName,
            patientRecordId,
            patientName,
            doctorId,
            documentIds,
            [],
            [],
            undefined,
            targetMonitoringDocumentId,
            accountId ?? null,
        );

        if (transcribeError) {
            Sentry.captureException(transcribeError);
            return { error: true };
        }

        return { error: null };
    } catch (error: any) {
        Sentry.captureException(error);
        return { error: true };
    }
};

export const downloadAudioFileFromSupabase = async (
    accountId: string | undefined,
    tId: any,
    audioName: any,
): Promise<Blob | null> => {
    let pathPrefix = accountId;

    if (!pathPrefix) {
        const supabaseSession = await supabase.auth.getSession();
        pathPrefix = supabaseSession?.data?.session?.user?.id;
        // Capture once outcome of the fallback is known, so a single event distinguishes a
        // degraded-but-recovered call (fellBackToSessionId: true) from total failure (false) —
        // both were previously reported identically, before the fallback lookup even ran.
        Sentry.captureException(new Error("downloadAudioFileFromSupabase called without a resolved account_id"), {
            extra: { transcriptionId: tId, audioName, fellBackToSessionId: Boolean(pathPrefix) },
        });
    }

    // PR review finding: without this guard, a still-falsy pathPrefix (no account_id AND no
    // session) fell through to a literal "undefined/..." Storage lookup instead of failing fast
    // -- the only one of the fixed call sites (the other is uploadAudioFileToSupabase)
    // that didn't already short-circuit here.
    if (!pathPrefix) {
        return null;
    }

    const { data, error } = await supabase.storage.from("audio-storage").download(`${pathPrefix}/${tId}/${audioName}`);
    if (error) {
        Sentry.captureException(error);
        return null;
    }
    return data;
};

// IMPORT APPOINTMENT MAPPING
export const getImportMapping = async (): Promise<DataMapping | null> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("data_mappings")
            .select()
            .order("updated_at", { ascending: false })
            .limit(1)
            .maybeSingle();
    });

    if (error || !data) {
        Sentry.captureException(error);
        return null;
    }

    return data;
};

//GET CUSTOM MODELS
export const getCustomModels = async () => {
    const { data, error } = await supabase.from("custom_models").select("model_name, model_id");
    if (error) {
        Sentry.captureException(error);
    }
    return data;
};

//Prompt Fields
export const getCreatorPromptData = async (
    getJSON: boolean = false,
    getMeta: boolean = false,
    documentId: string | undefined,
    abortController: AbortSignal,
) => {
    try {
        // Import the new API function
        const { getCreatorPromptFields } = await import("./api/creatorPromptFields");
        // Call the new API endpoint
        return await getCreatorPromptFields(getJSON, getMeta, documentId, abortController);
    } catch (error) {
        Sentry.captureException(error);
        return [];
    }
};

/**
 * (inserts or updates) a new field into the creator_prompt_fields table.
 * @param field - The CreatorPromptField object to be inserted.
 * @returns A Promise that resolves to the inserted CreatorPromptField or null if the insertion fails.
 */
export const upsertField = async (
    field: CreatorPromptField,
    abortController: AbortSignal,
): Promise<CreatorPromptField | null> => {
    try {
        const {
            categories,
            customPrompt,
            customPromptOptions,
            description,
            displayOutput,
            displayTitle,
            display_transcript_options,
            example,
            example_prompts,
            other_names,
            ...cleanField
        } = field;

        const { upsertCreatorPromptFields } = await import("./api/creatorPromptFields");
        return await upsertCreatorPromptFields(cleanField, abortController);
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

/**
 * Deletes a field from the creator_prompt_fields table.
 * @param fieldId - The ID of the field to be deleted.
 * @param abortController - The AbortSignal to cancel the operation.
 * @returns A Promise that resolves to true if the deletion is successful, false otherwise.
 */
export const deleteField = async (fieldId: string, abortController: AbortSignal): Promise<boolean> => {
    try {
        const { deleteCreatorPromptFields } = await import("./api/creatorPromptFields");
        return await deleteCreatorPromptFields(fieldId, abortController);
    } catch (error) {
        Sentry.captureException(error);
        return false;
    }
};

/**
 * (inserts or updates) a new example prompt into the example_prompts table.
 * @param examplePrompt - The ExamplePrompt object to be inserted.
 * @returns A Promise that resolves to the inserted ExamplePrompt or null if the insertion fails.
 */
export const upsertExamplePrompt = async (examplePrompt: ExamplePrompt): Promise<ExamplePrompt | null> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase
            .from("example_prompts")
            .upsert(examplePrompt as any)
            .select();
    });

    if (error) {
        console.error("Error upserting example prompt:", error);
        return null;
    }

    return (data?.[0] as ExamplePrompt) || null;
};

/**
 * Deletes an example prompt from the example_prompts table.
 * @param examplePromptId - The ID of the example prompt to be deleted.
 * @returns A Promise that resolves to true if the deletion is successful, false otherwise.
 */
export const deleteExamplePrompt = async (examplePromptId: string): Promise<boolean> => {
    const { error } = await retryWrapper(async () => {
        return await supabase.from("example_prompts").delete().eq("id", examplePromptId);
    });

    if (error) {
        return false;
    }

    return true;
};

/**
 * Inserts a new creator prompt field category mapping into the creator_prompt_field_categories table.
 * @param creatorPromptFieldCategories - The CreatorPromptFieldCategory object to be inserted.
 * @returns A Promise that resolves to the inserted CreatorPromptFieldCategory or null if the insertion fails.
 */
export const insertCreatorPromptFieldCategories = async (
    creatorPromptFieldCategories: CreatorPromptFieldCategory,
): Promise<CreatorPromptFieldCategory | null> => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase.from("creator_prompt_field_categories").insert(creatorPromptFieldCategories).select();
    });

    if (error) {
        return null;
    }

    return (data?.[0] as CreatorPromptFieldCategory) || null;
};

/**
 * (inserts or updates) a custom field meta record in the custom_field_meta table.
 * @param customFieldMeta - The CustomFieldMeta object to be upserted.
 * @returns A Promise that resolves to the upserted CustomFieldMeta or null if the operation fails.
 */
// DEPRECATED - replaced by api/documentTypes.ts (EN-548). Kept for rollback; remove in follow-up.
export const upsertCustomFieldMeta = async (customFieldMeta: CustomFieldMeta): Promise<CustomFieldMeta | null> => {
    try {
        const { data, error } = await supabase
            .from("custom_field_meta")
            .upsert(customFieldMeta as any, {
                onConflict: "account_id,field_id,document_type_id",
            })
            .select();

        if (error) {
            throw error;
        }

        return (data?.[0] as CustomFieldMeta) || null;
    } catch (error) {
        Sentry.captureException(error);
        return null;
    }
};

export const getCreatorPromptCategories = async (abortController: AbortSignal) => {
    const { data, error } = await retryWrapper(async () => {
        return await supabase.from("creator_prompt_categories").select("id, display_name").abortSignal(abortController);
    });

    if (error) {
        return [];
    }

    return data ?? [];
};

export const getCreatorSectionNames = async (abortSignal: AbortSignal, accountId?: string): Promise<Set<string>> => {
    const { data, error } = await retryWrapper(async () => {
        let query = supabase
            .from("creator_prompt_fields")
            .select("field_name, field_other_names")
            .abortSignal(abortSignal);

        // If accountId is provided, filter by account
        if (accountId) {
            query = query.eq("account_id", accountId);
        }

        return await query;
    });

    if (error || !data || !Array.isArray(data) || data?.length === 0) {
        return new Set();
    }

    const allNames = data?.reduce((acc: string[], { field_name, field_other_names }) => {
        acc.push(field_name?.toLowerCase() || ""); // Add the section_name
        if (Array.isArray(field_other_names)) {
            // Ensure field_other_names is an array
            const otherNames = field_other_names.map((name: string) => name.toLowerCase());
            acc.push(...otherNames);
        }
        return acc;
    }, []);

    const uniqueNames = new Set(allNames);

    return uniqueNames;
};
